import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from './config/api';
import Spinner from 'react-bootstrap/Spinner';
import { FaCheckCircle, FaCalendarAlt, FaCheckSquare, FaChevronDown, FaClipboardList } from 'react-icons/fa';
import { Link } from 'react-router-dom';

function BuildingCard({ building, onClick, isClickable = true }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Format date helper
  const formatDate = (dateObj) => {
    if (!dateObj) return '';
    if (typeof dateObj === 'string') {
      try {
        const d = new Date(dateObj);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch {
        return dateObj;
      }
    }
    if (typeof dateObj === 'object' && dateObj.iso) {
      try {
        const d = new Date(dateObj.iso);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch {
        return dateObj.iso;
      }
    }
    return '';
  };

  const start = formatDate(building.startTerm);
  const end = formatDate(building.endTerm);
  const cycle = building.cycle || '';

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        cursor: isClickable ? 'pointer' : 'default',
        opacity: isClickable ? 1 : 0.7,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        padding: isMobile ? '1rem 1rem' : '0.75rem 0.85rem',
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '1rem' : '0.75rem',
        minHeight: isMobile ? '80px' : 'auto',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        if (isClickable) {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
      }}
      onClick={isClickable ? onClick : undefined}
    >
      <div
        style={{
          position: 'absolute',
          top: '0.5rem',
          right: '0.5rem',
          fontSize: '0.7rem',
          color: '#9ca3af',
          fontWeight: 500,
        }}
        className="building-id-display"
      >
        {building.buildingId || 'Not Found'}
      </div>
      {/* Building icon with cycle pill below */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.5rem',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: isMobile ? 64 : 56,
            height: isMobile ? 64 : 56,
            borderRadius: '12px',
            backgroundColor: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ fontSize: isMobile ? '2rem' : '1.75rem' }}>🏢</div>
        </div>
        {cycle && (
          <div
            style={{
              fontSize: isMobile ? '0.75rem' : '0.7rem',
              color: '#2563eb',
              fontWeight: 600,
              padding: isMobile ? '0.3rem 0.5rem' : '0.25rem 0.4rem',
              backgroundColor: '#eff6ff',
              borderRadius: '6px',
              whiteSpace: 'nowrap',
            }}
          >
            {cycle}
          </div>
        )}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontWeight: 600,
            color: '#111827',
            fontSize: isMobile ? '1.1rem' : '0.95rem',
            marginBottom: isMobile ? '0.35rem' : '0.25rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {building.name}
        </div>
        {building.address && (
          <div
            style={{
              fontSize: isMobile ? '0.9rem' : '0.8rem',
              color: '#6b7280',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginBottom: (start || end) ? (isMobile ? '0.4rem' : '0.3rem') : 0,
            }}
          >
            {building.address}
          </div>
        )}
        {(start || end) && (
          <div
            style={{
              fontSize: isMobile ? '0.8rem' : '0.75rem',
              color: '#6b7280',
              fontWeight: 500,
              padding: isMobile ? '0.35rem 0.5rem' : '0.3rem 0.5rem',
              backgroundColor: '#f9fafb',
              borderRadius: '6px',
              display: 'inline-block',
            }}
          >
            {start && end ? `${start} - ${end}` : start ? `From ${start}` : end ? `Until ${end}` : 'No dates'}
          </div>
        )}
      </div>
      {building.maintenanceType && (
        <div
          style={{
            position: 'absolute',
            bottom: '0.5rem',
            right: '0.5rem',
            fontSize: isMobile ? '0.75rem' : '0.7rem',
            color: '#ea580c',
            fontWeight: 600,
            padding: isMobile ? '0.3rem 0.5rem' : '0.25rem 0.4rem',
            backgroundColor: '#fff7ed',
            borderRadius: '6px',
            whiteSpace: 'nowrap',
          }}
          className="maintenance-type-display"
        >
          {building.maintenanceType}
        </div>
      )}
    </div>
  );
}

export default function FieldHome() {
  const [activeBuildings, setActiveBuildings] = useState([]);
  const [upcomingBuildings, setUpcomingBuildings] = useState([]);
  const [pastBuildings, setPastBuildings] = useState([]);
  const [showActive, setShowActive] = useState(true);
  const [showUpcoming, setShowUpcoming] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isCancelled = false;

    async function loadBuildings() {
      setIsLoading(true);
      try {
        const resp = await fetch(`${API_BASE_URL}/api/field/buildings`);
        if (!resp.ok) {
          throw new Error(`Failed to load buildings: ${resp.status}`);
        }
        const data = await resp.json();
        const buildings = Array.isArray(data.buildings) ? data.buildings : [];

        const withPhotos = buildings.map((b) => ({
          id: b.id,
          buildingId: b.buildingId,
          name: b.name,
          address: b.address || '',
          photoUrl: b.photoUrl || null,
          status: b.status || '', // "Active", "Upcoming", or "Past"
          maintenanceID: b.maintenanceID || null,
          startTerm: b.startTerm || null,
          endTerm: b.endTerm || null,
          cycle: b.cycle || '',
          maintenanceType: b.maintenanceType || '',
        }));

        const active = withPhotos.filter((b) => b.status === 'Active');
        const getStartStr = (term) => {
          if (!term) return '';
          if (typeof term === 'string') return term;
          if (typeof term === 'object' && term.iso) return term.iso;
          return String(term);
        };
        const upcoming = withPhotos
          .filter((b) => b.status === 'Upcoming')
          .sort((a, b) => getStartStr(a.startTerm).localeCompare(getStartStr(b.startTerm)));
        const past = withPhotos.filter((b) => b.status === 'Past');

        if (!isCancelled) {
          setActiveBuildings(active);
          setUpcomingBuildings(upcoming);
          setPastBuildings(past);
          setError(null);
        }
      } catch (e) {
        console.error('[FieldHome] Failed to fetch buildings:', e);
        if (!isCancelled) {
          setActiveBuildings([]);
          setUpcomingBuildings([]);
          setPastBuildings([]);
          setError('Failed to load buildings. Please check your internet connection and try again.');
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    loadBuildings();
    return () => {
      isCancelled = true;
    };
  }, []);

  return (
    <div style={{ padding: '1.5rem 2rem' }}>
      <style>{`
        @media (min-width: 768px) {
          .building-id-display {
            display: block !important;
          }
          .maintenance-schedule-btn {
            display: flex !important;
          }
        }
        @media (max-width: 767px) {
          .building-id-display {
            display: none !important;
          }
          .maintenance-schedule-btn {
            display: none !important;
          }
        }
      `}</style>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <h1
            style={{
              fontSize: '1.75rem',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '0.25rem',
            }}
          >
            Hudson In Suite Maintenance
          </h1>
          <Link 
            to="/maintenance-schedule" 
            className="maintenance-schedule-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#2563eb',
              color: '#ffffff',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: 500,
              fontSize: '0.9rem',
              transition: 'background-color 0.15s ease',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
          >
            <FaClipboardList />
            Maintenance Schedule
          </Link>
        </div>

        {isLoading && (
          <div style={{ 
            marginBottom: '2rem', 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '3rem 1rem',
          }}>
            <Spinner animation="border" role="status" style={{ color: '#2563eb', marginBottom: '1rem' }}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p style={{ color: '#6b7280', fontSize: '1rem', fontWeight: 500, margin: 0 }}>
              Loading buildings...
            </p>
          </div>
        )}

        {error && (
          <div style={{ 
            marginBottom: '1.5rem', 
            padding: '1rem', 
            backgroundColor: '#fef2f2', 
            border: '1px solid #fecaca',
            borderRadius: '0.5rem',
            color: '#991b1b', 
            fontSize: '0.9rem' 
          }}>
            {error}
          </div>
        )}

        {/* Active buildings */}
        <div style={{ marginBottom: '0.75rem' }}>
          <button
            onClick={() => setShowActive((prev) => !prev)}
            aria-expanded={showActive}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: '#111827',
                  marginBottom: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <FaCheckCircle style={{ color: '#10b981', fontSize: '1.1rem' }} />
                Active Buildings ({activeBuildings.length})
              </div>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0 }}>
                Buildings we are currently performing maintenance at.
              </p>
            </div>
            <FaChevronDown
              style={{
                color: '#6b7280',
                transition: 'transform 0.2s ease',
                transform: showActive ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </button>
        </div>

        {showActive && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              marginBottom: '3.5rem',
            }}
          >
            {activeBuildings.map((b) => (
              <BuildingCard
                key={b.id}
                building={b}
                isClickable={true}
                onClick={() =>
                  navigate(
                    `/schedule/buildings/${encodeURIComponent(b.id)}${
                      b.maintenanceID ? `?maintenanceID=${encodeURIComponent(b.maintenanceID)}` : ''
                    }`
                  )
                }
              />
            ))}
          </div>
        )}

        {/* Upcoming buildings section */}
        {upcomingBuildings.length > 0 && (
          <>
            <div
              style={{
                borderTop: '1px solid #e5e7eb',
                paddingTop: '2rem',
                marginTop: '2rem',
                marginBottom: '0.75rem',
              }}
            >
              <button
                onClick={() => setShowUpcoming((prev) => !prev)}
                aria-expanded={showUpcoming}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: '#111827',
                      marginBottom: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <FaCalendarAlt style={{ color: '#3b82f6', fontSize: '1.1rem' }} />
                    Upcoming Buildings ({upcomingBuildings.length})
                  </div>
                  <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0 }}>
                    Maintenance cycles scheduled to begin soon.
                  </p>
                </div>
                <FaChevronDown
                  style={{
                    color: '#6b7280',
                    transition: 'transform 0.2s ease',
                    transform: showUpcoming ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </button>
            </div>

            {showUpcoming && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                  marginBottom: '3.5rem',
                }}
              >
                {upcomingBuildings.map((b) => (
                  <BuildingCard
                    key={b.id}
                    building={b}
                    isClickable={false}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Completed buildings section */}
        <div
          style={{
            borderTop: '1px solid #e5e7eb',
            paddingTop: '2rem',
            marginTop: '2rem',
            marginBottom: '0.75rem',
          }}
        >
          <button
            onClick={() => setShowCompleted((prev) => !prev)}
            aria-expanded={showCompleted}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: '#111827',
                  marginBottom: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <FaCheckSquare style={{ color: '#6b7280', fontSize: '1.1rem' }} />
                Completed Buildings ({pastBuildings.length})
              </div>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0 }}>
                Sites where maintenance runs have been completed.
              </p>
            </div>
            <FaChevronDown
              style={{
                color: '#6b7280',
                transition: 'transform 0.2s ease',
                transform: showCompleted ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </button>
        </div>

        {showCompleted && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
            }}
          >
            {pastBuildings.map((b) => (
              <BuildingCard
                key={b.id}
                building={b}
                isClickable={true}
                onClick={() =>
                  navigate(
                    `/schedule/buildings/${encodeURIComponent(b.id)}${
                      b.maintenanceID ? `?maintenanceID=${encodeURIComponent(b.maintenanceID)}` : ''
                    }`
                  )
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


